import { obtenerMensajeError } from "utils/apiErrors";
import { useState, useEffect, useMemo } from "react";
import ProcesosApi from "api/procesosApi";
import * as XLSX from "xlsx";

// Función para formatear moneda
const formatCurrency = (amount) => {
  if (!amount) return { formatted: "$0" };
  return {
    formatted: new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount),
  };
};

// Función para exportar CSV
// const exportToCSV = (data, filename) => {
//   const headers = Object.keys(data[0] || {}).join(",");
//   const csvContent =
//     "data:text/csv;charset=utf-8," +
//     headers +
//     "\n" +
//     data
//       .map((row) =>
//         Object.values(row)
//           .map((val) => `"${val}"`)
//           .join(",")
//       )
//       .join("\n");

//   const link = document.createElement("a");
//   link.setAttribute("href", encodeURI(csvContent));
//   link.setAttribute("download", `${filename}.csv`);
//   document.body.appendChild(link);
//   link.click();
//   document.body.removeChild(link);
// };

function CardCartera() {
  const [totales, setTotales] = useState({
    cantidadPlanesPago: 0,
    cantidadPlanesPagoActivos: 0,
    cantidadPlanesPagoAutorizados: 0,
    montoTotalGeneral: 0,
    saldoPendienteGeneral: 0,
    ingresoRecaudadoGeneral: 0,
    procesosDatos: [],
    ingresosPendientes: [],
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [estadoFilter, setEstadoFilter] = useState("");
  const [showOnlyPending, setShowOnlyPending] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  // Nuevos estados para filtros avanzados
  const [filtrosAvanzados, setFiltrosAvanzados] = useState({
    fechaDesde: "",
    fechaHasta: "",
    estado: "",
    soloVencidos: false,
    proximosDias: 7,
  });
  //const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  useEffect(() => {
    fetchDatosCartera();
  }, []);

  const fetchDatosCartera = async () => {
    setLoading(true);
    setError(null);
    try {
      // 1. Obtener filtros disponibles (opcional)
      try {
        const filtrosDisponibles = await ProcesosApi.obtenerFiltrosCartera();
        console.log("Filtros disponibles:", filtrosDisponibles.data);
        // Puedes usar estos filtros para poblar dropdowns dinámicamente
      } catch (filtrosError) {
        console.warn("No se pudieron cargar los filtros:", filtrosError);
        // Continúa sin filtros dinámicos
      }

      // 2. Obtener datos principales de cartera
      //const respCartera = await ProcesosApi.obtenerEstadisticas("CARTERA");
      // Si quieres usar filtros, descomenta la línea de abajo:
      const respCartera = await ProcesosApi.obtenerEstadisticas("CARTERA", {
        fecha_desde: filtrosAvanzados.fechaDesde, // "2024-01-01",
        solo_vencidos: filtrosAvanzados.soloVencidos,
        proximos_dias: filtrosAvanzados.proximosDias,
      });

      const data = respCartera.data[0];
      setTotales({
        cantidadPlanesPago: data.cantidadPlanesPago,
        cantidadPlanesPagoActivos: data.cantidadPlanesPagoActivos,
        cantidadPlanesPagoAutorizados: data.cantidadPlanesPagoAutorizados,
        montoTotalGeneral: data.montoTotalGeneral,
        saldoPendienteGeneral: data.saldoPendienteGeneral,
        ingresoRecaudadoGeneral: data.ingresoRecaudadoGeneral,
        procesosDatos: data.procesosDatos || [],
        ingresosPendientes: data.ingresosPendientes || [],
        // Nuevos campos si los implementaste en el backend:
        ingresosPendientesVencidos: data.ingresosPendientesVencidos || [],
        ingresosPendientesProximos: data.ingresosPendientesProximos || [],
        estadisticas: data.estadisticas || {},
      });
    } catch (error) {
      console.error("Error al obtener los datos del backend:", error);
      setError(obtenerMensajeError(error, "Error al cargar los datos de cartera"));
    } finally {
      setLoading(false);
    }
  };

  // Función para aplicar filtros desde el frontend
  const aplicarFiltrosAvanzados = async (filtros) => {
    setLoading(true);
    try {
      const respCartera = await ProcesosApi.obtenerEstadisticas("CARTERA", {
        fecha_desde: filtros.fechaDesde,
        fecha_hasta: filtros.fechaHasta,
        estado: filtros.estado,
        solo_vencidos: filtros.soloVencidos,
        proximos_dias: filtros.proximosDias || 7,
      });

      const data = respCartera.data[0];
      setTotales({
        cantidadPlanesPago: data.cantidadPlanesPago,
        cantidadPlanesPagoActivos: data.cantidadPlanesPagoActivos,
        cantidadPlanesPagoAutorizados: data.cantidadPlanesPagoAutorizados,
        montoTotalGeneral: data.montoTotalGeneral,
        saldoPendienteGeneral: data.saldoPendienteGeneral,
        ingresoRecaudadoGeneral: data.ingresoRecaudadoGeneral,
        procesosDatos: data.procesosDatos || [],
        ingresosPendientes: data.ingresosPendientes || [],
        // Nuevos campos si los implementaste en el backend:
        ingresosPendientesVencidos: data.ingresosPendientesVencidos || [],
        ingresosPendientesProximos: data.ingresosPendientesProximos || [],
        estadisticas: data.estadisticas || {},
      });
    } catch (error) {
      setError(obtenerMensajeError(error, "Error al aplicar filtros"));
    } finally {
      setLoading(false);
    }
  };

  // Filtros para procesos
  const filteredProcesos = useMemo(() => {
    return totales.procesosDatos.filter((proceso) => {
      const matchesSearch =
        !searchTerm ||
        proceso.proceso.toLowerCase().includes(searchTerm.toLowerCase()) ||
        proceso.cliente.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesEstado = !estadoFilter || proceso.estado === estadoFilter;
      const matchesPending = !showOnlyPending || proceso.saldo > 0;

      return matchesSearch && matchesEstado && matchesPending;
    });
  }, [totales.procesosDatos, searchTerm, estadoFilter, showOnlyPending]);

  // Filtros para ingresos
  const filteredIngresos = useMemo(() => {
    return totales.ingresosPendientes.filter((ingreso) => {
      const matchesSearch =
        !searchTerm ||
        ingreso.proceso.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ingreso.cliente.toLowerCase().includes(searchTerm.toLowerCase());

      return matchesSearch;
    });
  }, [totales.ingresosPendientes, searchTerm]);

  // Obtener datos de la pestaña actual
  const getCurrentTabData = () => {
    if (activeTab === 0) return filteredProcesos;
    if (activeTab === 1) return filteredIngresos;
    // Pestaña 2: Pagos vencidos
    if (activeTab === 2) {
      return filteredIngresos.filter((ing) => {
        const fechaCompromiso = new Date(ing.fecha_compromiso);
        const hoy = new Date();
        return Math.floor((hoy - fechaCompromiso) / (1000 * 60 * 60 * 24)) > 0;
      });
    }
    // Pestaña 3: Próximos 7 días
    if (activeTab === 3) {
      return filteredIngresos.filter((ing) => {
        const fechaCompromiso = new Date(ing.fecha_compromiso);
        const hoy = new Date();
        const diasDiff = Math.floor((fechaCompromiso - hoy) / (1000 * 60 * 60 * 24));
        return diasDiff >= 0 && diasDiff <= 7;
      });
    }
    return [];
  };

  const currentData = getCurrentTabData();
  const totalPages = Math.ceil(currentData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = currentData.slice(startIndex, startIndex + itemsPerPage);

  const exportToExcel = (data, filename) => {
    // Crear un nuevo workbook
    const workbook = XLSX.utils.book_new();
    // Crear worksheet con los datos
    const worksheet = XLSX.utils.json_to_sheet(data);
    // Agregar el worksheet al workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, "Cartera");
    // Generar el archivo y descargarlo
    XLSX.writeFile(workbook, `${filename}.xlsx`);
  };

  const handleExport = () => {
    const dataToExport = currentData.map((item) => {
      if (activeTab === 0) {
        const avance = item.monto > 0 ? ((item.monto - item.saldo) / item.monto) * 100 : 0;
        return {
          Proceso: item.proceso,
          Cliente: item.cliente,
          Monto: item.monto,
          Saldo: item.saldo,
          Estado: item.estado,
          Porcentaje_Pagado: avance.toFixed(1) + "%",
        };
      } else {
        const fechaCompromiso = new Date(item.fecha_compromiso);
        const hoy = new Date();
        const diasVencido = Math.floor((hoy - fechaCompromiso) / (1000 * 60 * 60 * 24));
        return {
          Fecha_Compromiso: fechaCompromiso.toLocaleDateString("es-CO"),
          Proceso: item.proceso,
          Cliente: item.cliente,
          Valor: item.valor,
          Celular: item.cliente_celular,
          Email: item.cliente_email,
          Dias_Vencido: diasVencido > 0 ? diasVencido : 0,
        };
      }
    });

    const tabNames = ["Procesos", "Pagos_Pendientes", "Pagos_Vencidos", "Proximos_Pagos"];
    //exportToCSV(dataToExport, `cartera_${tabNames[activeTab]}`);
    exportToExcel(dataToExport, `cartera_${tabNames[activeTab]}`);
  };

  const clearFilters = () => {
    setSearchTerm("");
    setEstadoFilter("");
    setShowOnlyPending(false);
    setCurrentPage(1);
    setFiltrosAvanzados({
      fechaDesde: "",
      fechaHasta: "",
      estado: "",
      soloVencidos: false,
      proximosDias: 7,
    });
    fetchDatosCartera();
  };

  if (loading) {
    return (
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "8px",
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
          padding: "40px",
          textAlign: "center",
        }}
      >
        <div
          style={{
            width: "40px",
            height: "40px",
            border: "4px solid #f3f3f3",
            borderTop: "4px solid #3498db",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
            margin: "0 auto 16px",
          }}
        ></div>
        <p style={{ color: "#666", margin: 0 }}>Cargando datos de cartera...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "8px",
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
          padding: "24px",
        }}
      >
        <div
          style={{
            backgroundColor: "#fee2e2",
            border: "1px solid #fecaca",
            borderRadius: "6px",
            padding: "16px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span style={{ color: "#dc2626" }}>{error}</span>
          <button
            onClick={fetchDatosCartera}
            style={{
              padding: "8px 16px",
              backgroundColor: "#dc2626",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  const metrics = {
    totalRecuperacion:
      totales.montoTotalGeneral > 0
        ? ((totales.ingresoRecaudadoGeneral / totales.montoTotalGeneral) * 100).toFixed(1)
        : 0,
    totalPendiente:
      totales.montoTotalGeneral > 0
        ? ((totales.saldoPendienteGeneral / totales.montoTotalGeneral) * 100).toFixed(1)
        : 0,
  };

  return (
    <div>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          .card {
            background-color: white;
            border-radius: 8px;
            box-shadow: 0 2px 10px rgba(0,0,0,0.1);
            overflow: hidden;
          }
          .btn {
            padding: 4px 8px;
            border: 1px solid #d1d5db;
            border-radius: 6px;
            background: white;
            cursor: pointer;
            font-size: 14px;
            transition: background-color 0.2s;
          }
          .btn:hover {
            background-color: #f9fafb;
          }
          .btn-primary {
            background-color: #3b82f6;
            border-color: #3b82f6;
            color: white;
          }
          .btn-primary:hover {
            background-color: #2563eb;
          }
          .tab-button {
            padding: 12px 16px;
            border: none;
            background: none;
            cursor: pointer;
            border-bottom: 2px solid transparent;
            transition: all 0.2s;
            font-size: 20px;
            font-weight: 500;
          }
          .tab-button.active {
            border-bottom-color: #3b82f6;
            color: #3b82f6;
            font-weight: 600;
          }
          .tab-button:hover {
            background-color: #f9fafb;
          }
          .input {
            padding: 4px 6px;
            border: 1px solid #d1d5db;
            border-radius: 6px;
            font-size: 14px;
            width: 100%;
          }
          .input:focus {
            outline: none;
            border-color: #3b82f6;
            box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
          }
          .table {
            width: 100%;
            border-collapse: collapse;
            font-size: 13px;
          }
          .table th,
          .table td {
            padding: 3px 4px;
            text-align: left;
            border-bottom: 1px solid #e5e7eb;
          }
          .table th {
            background-color: #f9fafb;
            font-weight: 600;
            font-size: 11px;
            text-transform: uppercase;
            color: #374151;
            padding: 8px 8px;
          }
          .table tr:hover {
            background-color: #f9fafb;
          }
          .chip {
            display: inline-flex;
            align-items: center;
            gap: 2px;
            padding: 2px 4px;
            border-radius: 12px;
            font-size: 12px;
            font-weight: 500;
          }
          .chip-success {
            background-color: #dcfce7;
            color: #166534;
          }
          .chip-error {
            background-color: #fee2e2;
            color: #dc2626;
          }
          .chip-warning {
            background-color: #fef3c7;
            color: #d97706;
          }
          .chip-primary {
            background-color: #dbeafe;
            color: #1d4ed8;
          }
          .progress-bar {
            width: 100%;
            height: 16px;
            background-color: #e5e7eb;
            border-radius: 3px;
            overflow: hidden;
          }
          .progress-fill {
            height: 100%;
            transition: width 0.3s ease;
          }
        `}
      </style>

      <div className="card">
        {/* Header */}
        <div style={{ padding: "6px", borderBottom: "1px solid #e5e7eb" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              marginBottom: "2px",
            }}
          >
            <div>
              <h2
                style={{
                  margin: "0 0 2px 0",
                  fontSize: "30px",
                  fontWeight: "bold",
                  color: "#1f2937",
                }}
              >
                Panel de Control de Cartera
              </h2>
            </div>
            <div style={{ display: "flex", gap: "4px" }}>
              <button onClick={fetchDatosCartera} className="btn" title="Actualizar">
                🔄
              </button>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={showFilters ? "btn btn-primary" : "btn"}
                title="Filtros"
              >
                🔍
              </button>
              <button onClick={handleExport} className="btn">
                📥 Exportar Excel
              </button>
            </div>
            <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
              <span
                style={{
                  padding: "4px 8px",
                  backgroundColor: "#f3f4f6",
                  borderRadius: "12px",
                  fontSize: "16px",
                  fontWeight: "500",
                }}
              >
                ✓ {totales.cantidadPlanesPago} Creados
              </span>
              <span
                style={{
                  padding: "4px 8px",
                  backgroundColor: "#dcfce7",
                  color: "#166534",
                  borderRadius: "12px",
                  fontSize: "16px",
                  fontWeight: "500",
                }}
              >
                ↗ {totales.cantidadPlanesPagoActivos} Activos
              </span>
              <span
                style={{
                  padding: "4px 8px",
                  backgroundColor: "#dbeafe",
                  color: "#1d4ed8",
                  borderRadius: "12px",
                  fontSize: "16px",
                  fontWeight: "500",
                }}
              >
                📋 {totales.cantidadPlanesPagoAutorizados} Autorizados
              </span>
            </div>
          </div>

          {/* Métricas */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
              gap: "4px",
            }}
          >
            <div
              style={{
                backgroundColor: "#dcfce7",
                border: "4px solid #bbf7d0",
                borderRadius: "8px",
                //padding: "2px",
                textAlign: "center",
              }}
            >
              <div style={{ fontSize: "20px", color: "#6b7280" }}>Monto Total Contratado</div>
              <div style={{ fontSize: "70px", marginBottom: "-12px" }}>🏢</div>
              <div
                style={{
                  fontSize: "30px",
                  fontWeight: "bold",
                  color: "#166534",
                  marginBottom: "4px",
                }}
              >
                {formatCurrency(totales.montoTotalGeneral).formatted}
              </div>
            </div>

            <div
              style={{
                backgroundColor: "#fef3c7",
                border: "4px solid #fde68a",
                borderRadius: "8px",
                //padding: "2px",
                textAlign: "center",
              }}
            >
              <div style={{ fontSize: "20px", color: "#6b7280" }}>
                Saldo Pendiente ({metrics.totalPendiente}%)
              </div>
              <div style={{ fontSize: "70px", marginBottom: "-12px" }}>📈</div>
              <div
                style={{
                  fontSize: "30px",
                  fontWeight: "bold",
                  color: "#d97706",
                  marginBottom: "4px",
                }}
              >
                {formatCurrency(totales.saldoPendienteGeneral).formatted}
              </div>
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{
                    width: `${metrics.totalPendiente}%`,
                    backgroundColor: "#f59e0b",
                  }}
                ></div>
              </div>
            </div>

            <div
              style={{
                backgroundColor: "#dbeafe",
                border: "4px solid #93c5fd",
                borderRadius: "8px",
                //padding: "2px",
                textAlign: "center",
              }}
            >
              <div style={{ fontSize: "20px", color: "#6b7280" }}>
                Ingreso Recaudado ({metrics.totalRecuperacion}%)
              </div>
              <div style={{ fontSize: "70px", marginBottom: "-12px" }}>💰</div>
              <div
                style={{
                  fontSize: "30px",
                  fontWeight: "bold",
                  color: "#1d4ed8",
                  marginBottom: "4px",
                }}
              >
                {formatCurrency(totales.ingresoRecaudadoGeneral).formatted}
              </div>
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{
                    width: `${metrics.totalRecuperacion}%`,
                    backgroundColor: "#3b82f6",
                  }}
                ></div>
              </div>
            </div>
          </div>
        </div>

        {/* Filtros */}
        {showFilters && (
          <div
            style={{
              padding: "16px",
              backgroundColor: "#f9fafb",
              borderBottom: "1px solid #e5e7eb",
            }}
          >
            <div
              style={{
                marginTop: "16px",
                padding: "16px",
                backgroundColor: "#f3f4f6",
                borderRadius: "6px",
              }}
            >
              <h4 style={{ margin: "0 0 12px 0", fontSize: "14px", fontWeight: "600" }}>
                Filtros Avanzados
              </h4>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                  gap: "12px",
                }}
              >
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "4px",
                      fontSize: "12px",
                      fontWeight: "500",
                    }}
                  >
                    Fecha Desde:
                  </label>
                  <input
                    type="date"
                    value={filtrosAvanzados.fechaDesde}
                    onChange={(e) =>
                      setFiltrosAvanzados({ ...filtrosAvanzados, fechaDesde: e.target.value })
                    }
                    className="input"
                  />
                </div>
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "4px",
                      fontSize: "12px",
                      fontWeight: "500",
                    }}
                  >
                    Fecha Hasta:
                  </label>
                  <input
                    type="date"
                    value={filtrosAvanzados.fechaHasta}
                    onChange={(e) =>
                      setFiltrosAvanzados({ ...filtrosAvanzados, fechaHasta: e.target.value })
                    }
                    className="input"
                  />
                </div>
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "4px",
                      fontSize: "12px",
                      fontWeight: "500",
                    }}
                  >
                    Próximos días:
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="30"
                    value={filtrosAvanzados.proximosDias}
                    onChange={(e) =>
                      setFiltrosAvanzados({
                        ...filtrosAvanzados,
                        proximosDias: parseInt(e.target.value),
                      })
                    }
                    className="input"
                  />
                </div>
              </div>
              <div style={{ marginTop: "12px" }}>
                <label
                  style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "14px" }}
                >
                  <input
                    type="checkbox"
                    checked={filtrosAvanzados.soloVencidos}
                    onChange={(e) =>
                      setFiltrosAvanzados({ ...filtrosAvanzados, soloVencidos: e.target.checked })
                    }
                  />
                  Solo mostrar vencidos
                </label>
              </div>
              <div style={{ marginTop: "12px", display: "flex", gap: "4px" }}>
                <button
                  onClick={() => aplicarFiltrosAvanzados(filtrosAvanzados)}
                  className="btn btn-primary"
                >
                  🔍 Aplicar Filtros
                </button>
                <button
                  onClick={() => {
                    setFiltrosAvanzados({
                      fechaDesde: "",
                      fechaHasta: "",
                      estado: "",
                      soloVencidos: false,
                      proximosDias: 7,
                    });
                    fetchDatosCartera();
                  }}
                  className="btn"
                >
                  🔄 Resetear
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Controles básicos */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "12px",
            marginLeft: "12px",
          }}
        >
          <label style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "14px" }}>
            <input
              type="checkbox"
              checked={showOnlyPending}
              onChange={(e) => setShowOnlyPending(e.target.checked)}
            />
            Solo mostrar procesos pendientes
          </label>
          <button onClick={clearFilters} className="btn">
            ✖ Limpiar
          </button>
        </div>

        {/* Pestañas */}
        <div style={{ borderBottom: "1px solid #e5e7eb" }}>
          <div style={{ display: "flex", overflowX: "auto" }}>
            <button
              onClick={() => setActiveTab(0)}
              className={`tab-button ${activeTab === 0 ? "active" : ""}`}
            >
              Procesos ({filteredProcesos.length})
            </button>
            <button
              onClick={() => setActiveTab(1)}
              className={`tab-button ${activeTab === 1 ? "active" : ""}`}
            >
              Pagos Pendientes ({filteredIngresos.length})
            </button>
            <button
              onClick={() => setActiveTab(2)}
              className={`tab-button ${activeTab === 2 ? "active" : ""}`}
            >
              Vencidos (
              {
                filteredIngresos.filter((ing) => {
                  const fechaCompromiso = new Date(ing.fecha_compromiso);
                  const hoy = new Date();
                  return Math.floor((hoy - fechaCompromiso) / (1000 * 60 * 60 * 24)) > 0;
                }).length
              }
              )
            </button>
            <button
              onClick={() => setActiveTab(3)}
              className={`tab-button ${activeTab === 3 ? "active" : ""}`}
            >
              Próximos 7 días (
              {
                filteredIngresos.filter((ing) => {
                  const fechaCompromiso = new Date(ing.fecha_compromiso);
                  const hoy = new Date();
                  const diasDiff = Math.floor((fechaCompromiso - hoy) / (1000 * 60 * 60 * 24));
                  return diasDiff >= 0 && diasDiff <= 7;
                }).length
              }
              )
            </button>
          </div>
        </div>

        {/* Tabla */}
        <div style={{ padding: "8px" }}>
          <div style={{ maxHeight: "400px", overflowY: "auto" }}>
            <table className="table">
              <thead style={{ position: "sticky", top: 0, backgroundColor: "#f9fafb", zIndex: 1 }}>
                <tr>
                  {activeTab === 0 ? (
                    <>
                      <th>Proceso</th>
                      <th>Cliente</th>
                      <th style={{ textAlign: "right" }}>Monto</th>
                      <th style={{ textAlign: "right" }}>Saldo</th>
                      <th style={{ textAlign: "center" }}>Estado</th>
                      <th style={{ textAlign: "right" }}>% Avance</th>
                    </>
                  ) : (
                    <>
                      <th>Fecha</th>
                      <th>Proceso</th>
                      <th>Cliente</th>
                      <th style={{ textAlign: "right" }}>Valor</th>
                      <th style={{ textAlign: "center" }}>Estado</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((item) => {
                  if (activeTab === 0) {
                    const avance =
                      item.monto > 0 ? ((item.monto - item.saldo) / item.monto) * 100 : 0;
                    return (
                      <tr key={item.id}>
                        <td style={{ fontWeight: "500" }}>{item.proceso}</td>
                        <td>{item.cliente}</td>
                        <td style={{ textAlign: "right", fontWeight: "500", color: "#166534" }}>
                          {formatCurrency(item.monto).formatted}
                        </td>
                        <td
                          style={{
                            textAlign: "right",
                            fontWeight: "500",
                            color: item.saldo > 0 ? "#d97706" : "#166534",
                          }}
                        >
                          {formatCurrency(item.saldo).formatted}
                        </td>
                        <td style={{ textAlign: "center" }}>
                          <span
                            className={`chip ${
                              item.estado === "EXITOSO"
                                ? "chip-success"
                                : item.estado === "FALLIDO"
                                ? "chip-error"
                                : item.estado === "SUSPENDIDO"
                                ? "chip-warning"
                                : "chip-primary"
                            }`}
                          >
                            {item.estado === "EXITOSO"
                              ? "✓ "
                              : item.estado === "FALLIDO"
                              ? "✗ "
                              : item.estado === "SUSPENDIDO"
                              ? "⚠ "
                              : "⏱ "}
                            {item.estado}
                          </span>
                        </td>
                        <td style={{ textAlign: "right" }}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "flex-end",
                              gap: "4px",
                            }}
                          >
                            <div style={{ width: "60px" }}>
                              <div className="progress-bar">
                                <div
                                  className="progress-fill"
                                  style={{
                                    width: `${avance}%`,
                                    backgroundColor: avance === 100 ? "#22c55e" : "#3b82f6",
                                  }}
                                ></div>
                              </div>
                            </div>
                            <span style={{ fontSize: "12px", color: "#6b7280", minWidth: "35px" }}>
                              {avance.toFixed(0)}%
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  } else {
                    const fechaCompromiso = new Date(item.fecha_compromiso);
                    const hoy = new Date();
                    const diasVencido = Math.floor((hoy - fechaCompromiso) / (1000 * 60 * 60 * 24));

                    return (
                      <tr key={item.id}>
                        <td
                          style={{
                            fontWeight: "500",
                            color: diasVencido > 0 ? "#dc2626" : "#374151",
                          }}
                        >
                          {fechaCompromiso.toLocaleDateString("es-CO")}
                        </td>
                        <td>
                          <div style={{ fontWeight: "700" }}>{item.proceso}</div>
                        </td>
                        <td>
                          <div style={{ fontWeight: "700" }}>{item.cliente}</div>
                          <div style={{ fontSize: "11px", color: "#6b7280", marginTop: "1px" }}>
                            📱 {item.cliente_celular} • ✉️ {item.cliente_email}
                          </div>
                        </td>
                        <td
                          style={{
                            textAlign: "right",
                            fontWeight: "500",
                            color: "#1d4ed8",
                            width: "200px",
                          }}
                        >
                          {formatCurrency(item.valor).formatted}
                        </td>
                        <td style={{ textAlign: "center" }}>
                          <span
                            className={`chip ${diasVencido > 0 ? "chip-error" : "chip-success"}`}
                          >
                            {diasVencido > 0 ? "⚠" : "✓"}
                            {diasVencido > 0 ? `${diasVencido} días vencido` : "Al día"}
                          </span>
                        </td>
                      </tr>
                    );
                  }
                })}
              </tbody>
            </table>
          </div>

          {/* Paginación */}
          {totalPages > 1 && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginTop: "16px",
                paddingTop: "16px",
                borderTop: "1px solid #e5e7eb",
              }}
            >
              <div style={{ fontSize: "14px", color: "#6b7280" }}>
                Mostrando {startIndex + 1} a{" "}
                {Math.min(startIndex + itemsPerPage, currentData.length)} de {currentData.length}{" "}
                resultados
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(parseInt(e.target.value));
                    setCurrentPage(1);
                  }}
                  style={{
                    padding: "4px 8px",
                    border: "1px solid #d1d5db",
                    borderRadius: "4px",
                    fontSize: "14px",
                  }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>

                <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="btn"
                    style={{
                      opacity: currentPage === 1 ? 0.5 : 1,
                      cursor: currentPage === 1 ? "not-allowed" : "pointer",
                    }}
                  >
                    Anterior
                  </button>

                  <span style={{ padding: "4px 8px", fontSize: "14px", color: "#6b7280" }}>
                    Página {currentPage} de {totalPages}
                  </span>

                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="btn"
                    style={{
                      opacity: currentPage === totalPages ? 0.5 : 1,
                      cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                    }}
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CardCartera;
